/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Deletes a single Expense Inventory sampling line item by reusing the
 * createUpdateExpenseLineItems mutation with `deleted: true`, matching the
 * backend's dedicated soft-delete branch (reverses the inventory hold, sets
 * deleted_by__c on casesusage__c, and cleans up order_replenishment_data).
 * @module src/components/Inventory/expense/detail/ExpenseInventorySampling/hook/useDeleteExpenseInventoryLineItem
 */
import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import CREATE_UPDATE_EXPENSE_LINE_ITEMS from '../../../../../../graphql/mutations/createUpdateExpenseLineItems';
import { NOTIFY_TYPE, STALE_RECORD_MESSAGE } from '../../../../../../util/constants';
import { isStaleRecordError } from '../../../../../../util/optimisticLock';
import { SamplingLineItem } from '../ExpenseInventorySamplingTable';

interface UseDeleteExpenseInventoryLineItemProps {
  expenseInventoryDetail: any;
  refetch: () => Promise<any>;
  openSnackBar: (type: string, message: string) => void;
}

const useDeleteExpenseInventoryLineItem = ({
  expenseInventoryDetail,
  refetch,
  openSnackBar,
}: UseDeleteExpenseInventoryLineItemProps) => {
  const [deletingUniqueId, setDeletingUniqueId] = useState<string | null>(null);
  const [createUpdateExpenseLineItems, { loading: loadingDelete }] = useMutation(
    CREATE_UPDATE_EXPENSE_LINE_ITEMS
  );

  const onDeleteLineItem = useCallback(
    async (item: SamplingLineItem): Promise<void> => {
      if (!item.sfid && !item.externalId) {
        openSnackBar(
          NOTIFY_TYPE.ERROR,
          'Error: This part cannot be deleted yet. Please try again.'
        );
        return;
      }

      setDeletingUniqueId(item.uniqueId);
      try {
        await createUpdateExpenseLineItems({
          variables: {
            caseId: expenseInventoryDetail?.id,
            caseExternalId: expenseInventoryDetail?.caseExternalId,
            salesRepId: expenseInventoryDetail?.salesRepId,
            formType: 'New',
            lineItems: [
              {
                sfid: item.sfid,
                externalId: item.externalId,
                deleted: true,
              },
            ],
          },
        });

        await refetch();
      } catch (error) {
        if (isStaleRecordError(error)) {
          openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
        } else {
          openSnackBar(
            NOTIFY_TYPE.ERROR,
            (error as Error)?.message || 'Error: Expense Inventory part could not be deleted.'
          );
        }
      } finally {
        setDeletingUniqueId(null);
      }
    },
    [expenseInventoryDetail, createUpdateExpenseLineItems, refetch, openSnackBar]
  );

  return { onDeleteLineItem, loadingDelete, deletingUniqueId };
};

export default useDeleteExpenseInventoryLineItem;
